import { ChevronDownIcon, Settings2Icon } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MembersToolbarButton } from '@/components/common/members-management';
import {
  allCompanyMemberColumns,
  companyMemberColumnLabels,
  type CompanyMemberColumnKey,
  type CompanyMemberSortBy,
  type CompanyMemberSortDirection,
} from './company-member-display';

const sortLabels: Record<CompanyMemberSortBy, string> = {
  createdAt: 'Ajouts récents',
  email: 'E-mail',
  emailVerified: 'Vérification',
  name: 'Nom',
  role: 'Rôle',
  status: 'Statut',
  updatedAt: 'Dernière modification',
};

export function CompanyMembersSortControl({
  direction,
  onDirectionChange,
  onSortChange,
  sortBy,
}: {
  direction: CompanyMemberSortDirection;
  onDirectionChange: (value: CompanyMemberSortDirection) => void;
  onSortChange: (value: CompanyMemberSortBy) => void;
  sortBy: CompanyMemberSortBy;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <MembersToolbarButton className="justify-between xl:min-w-56">
          {sortLabels[sortBy]}
          <ChevronDownIcon data-icon="inline-end" />
        </MembersToolbarButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuGroup>
          <DropdownMenuLabel>Ordre d’affichage</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuRadioGroup
            value={sortBy}
            onValueChange={(value) => onSortChange(value as CompanyMemberSortBy)}
          >
            {Object.entries(sortLabels).map(([value, label]) => (
              <DropdownMenuRadioItem key={value} value={value}>{label}</DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>
        <DropdownMenuSeparator />
        <DropdownMenuGroup>
          <DropdownMenuRadioGroup
            value={direction}
            onValueChange={(value) => onDirectionChange(value as CompanyMemberSortDirection)}
          >
            <DropdownMenuRadioItem value="asc">Croissant</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="desc">Décroissant</DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function CompanyMembersColumnsControl({
  onToggle,
  visible,
}: {
  onToggle: (key: CompanyMemberColumnKey) => void;
  visible: ReadonlySet<CompanyMemberColumnKey>;
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <MembersToolbarButton aria-label="Choisir les colonnes">
          Colonnes
          <Settings2Icon data-icon="inline-end" />
        </MembersToolbarButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuGroup>
          <DropdownMenuLabel>Colonnes affichées</DropdownMenuLabel>
          <DropdownMenuSeparator />
          {allCompanyMemberColumns.map((key) => (
            <DropdownMenuCheckboxItem
              checked={visible.has(key)}
              disabled={key === 'member' || visible.size === 1}
              key={key}
              onCheckedChange={() => onToggle(key)}
              onSelect={(event) => event.preventDefault()}
            >
              {companyMemberColumnLabels[key]}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
